<template>
  <div>
    <div v-if="!loading">
      <h1 class="text-lg leading-tight font-bold pb-2 xl:text-2.5xl xl:pb-4">
        {{ $t('components.summary.title') }}
      </h1>

      <div v-if="errors.length" ref="error" class="w-full flex border border-gray-300 bg-white p-4 mt-2 mb-4">
        <div class="w-8 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" v-bind:svg-inline="''" v-bind:class="'w-4.5 h-4.5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.6 0H5.269L0 5.4v7.2L5.4 18h7.2l5.4-5.4V5.4zm1.665 12.189l-2.121 2.121-3.189-3.19-3.189 3.19-2.122-2.121L6.837 9l-3.19-3.189 2.122-2.122 3.189 3.19 3.189-3.19 2.121 2.122L11.08 9z" fill="#c00"/></svg>
        </div>
        <div class="pt-2px xl:pt-0">
          <p class="font-bold text-sm leading-none pb-2 xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <template>
        <div class="w-full mb-4 border border-sw_gray-300">
          <div class="p-3 text-sm font-bold leading-tight border-b border-sw_gray-300 xl:p-4 xl:text-lg">
            {{ $t('components.summary.table.vouchers.title') }}
          </div>

          <div
            v-for="appliedVoucher in redemption.appliedVouchers"
            :key="appliedVoucher.id"
            class="p-3 text-sm font-bold xl:p-4 xl:text-base"
          >
            <div class="flex justify-between">
              <div class="mr-3">
                {{ translateType(appliedVoucher.type) }}
                {{ $t('components.summary.table.vouchers.voucher_validity', { date: $d(new Date(appliedVoucher.validUntil)) }) }}
                - {{ appliedVoucher.code }}
              </div>
              <div class="flex items-center flex-shrink-0 font-numeric">
                {{ appliedVoucher.balanceInCents | lhFormatCurrency(currency, market) }}
                <button
                  class="flex-shrink-0 w-6 h-6 ml-2 transition-colors duration-300 ease-in-out hover:text-sw_primary"
                  :aria-label="$t('components.summary.flightVoucher.removeVoucher')"
                  @click.prevent="deleteVoucher(appliedVoucher)"
                >
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'fill-current'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M42.54 99h34.928a6 6 0 005.98-5.518L88.005 37h-56l4.555 56.482A6 6 0 0042.54 99zm29.898-51.07a1.5 1.5 0 112.996.14L73.56 87.406a1.5 1.5 0 01-1.496 1.43l-.072-.003a1.5 1.5 0 01-1.427-1.57zM58.5 48a1.5 1.5 0 013 0v39.334a1.5 1.5 0 01-3 0zm-12.508-1.5a1.492 1.492 0 011.57 1.43l1.872 39.332a1.5 1.5 0 01-1.426 1.57h-.073a1.5 1.5 0 01-1.497-1.427L44.565 48.07a1.5 1.5 0 011.427-1.57zm9.59-18.5v-1H31.997v6h56.008v-6H64.417v1h-8.834m0-6h8.833v5h-8.833z"/><path d="M55.583 27h8.833v1h-8.833z"/><path d="M55.583 27h8.833v1h-8.833z"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="shouldShowAddAnotherVoucher" class="mb-6">
          <div
            class="inline-flex items-center transition-colors duration-300 ease-in-out cursor-pointer text-sw_yellow_error-text hover:text-sw_gray-700"
            @click="showAddVoucherPopup = true"
          >
            <div class="flex-shrink-0 w-5 h-5 mr-2 cursor-pointer">
              <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'fill-current'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M96 71.973H71.973V96H24V48.027h24.027V24H96zm-30.014 18.04v-18.04h-17.96v-17.96h-18.04v36zm24.028-60.027h-36v36h36z"/></svg>
            </div>
            {{ $t('components.summary.link.add_more_voucher') }}
          </div>
        </div>

        <div class="w-full mb-4 border border-sw_gray-300">
          <div class="p-3 text-sm font-bold leading-tight border-b border-sw_gray-300 xl:p-4 xl:text-lg">
            {{ $t('components.summary.table.ticket.title') }}
          </div>

          <div class="p-3 text-sm font-bold xl:p-4 xl:text-base">
            <div class="flex justify-between">
              <div class="mr-3">
                {{ $t('components.summary.table.ticket.ticketPrice') }}
              </div>
              <div class="font-bold font-numeric">
                {{ redemption.amount | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="w-full mb-8 border border-sw_gray-300">
        <div class="p-3 text-sm font-bold leading-tight border-b border-sw_gray-300 xl:p-4 xl:text-lg">
          {{ $t('components.summary.finalSummary.title') }}
        </div>

        <div class="p-3 text-sm xl:p-4 xl:text-base">
          <div class="flex justify-between pb-2">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>
          <div class="flex justify-between">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
            </div>
          </div>
        </div>

        <div class="p-3 text-sm font-bold bg-sw_gray-50 xl:p-4 xl:text-lg">
          <div class="flex justify-between ">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.remainingFlightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ remainingFlightVoucherBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>
          <div class="flex justify-between pt-2">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="paymentRequired" class="flex justify-end xl:pt-6">
      <Button
        :disabled="loading"
        :loading="loading"
        class="w-full xl:w-auto"
        @click="continueToCCPayment"
      >
        {{ $t('components.summary.continueToCreditCardButton') }}
      </Button>
    </div>

    <div v-else class="flex justify-end xl:pt-6">
      <Button
        :disabled="loading"
        :loading="loading"
        class="w-full xl:w-auto"
        @click="confirmRedemption"
      >
        {{ $t('components.summary.submitButton') }}
      </Button>
    </div>

    <RemoveVoucherPopup
      v-if="voucherToDelete"
      :value="true"
      :voucher="voucherToDelete"
      @input="onRemoveVoucherInput"
    />
    <AddVoucherPopup v-model="showAddVoucherPopup" />
    <CancelRedemptionPopup v-model="showExitPopup" />
  </div>
</template>

<script lang="ts">
import Summary from '~psp-pages/base/redemption/summary.ts'

import Button from '~brand-components/swiss/button/button.vue'
import AddVoucherPopup from '~app/psp/swiss/add-voucher-popup.vue'
import CancelRedemptionPopup from '~app/psp/swiss/cancel-redemption-popup.vue'
import RemoveVoucherPopup from '~app/psp/swiss/remove-voucher-popup.vue'
import Payment from './payment.vue'

export default Summary.extend({
  components: {
    RemoveVoucherPopup,
    Payment,
    Button,
    AddVoucherPopup,
    CancelRedemptionPopup,
  },
})
</script>
